import React from 'react';
import { checkIsOutdated, clearReloadGuard, reloadOnce } from '@/app/utils/buildVersion.ts';

/**
 * Heuristic to detect chunk/dynamic-import load failures vs other errors.
 * Vite, Webpack and the browsers produce different messages, so we check for known patterns.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    // Vite
    msg.includes('failed to fetch dynamically imported module') ||
    // Vite CSS preload
    msg.includes('unable to preload css') ||
    // Webpack
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    // Generic network failures during dynamic import
    msg.includes('dynamically imported module') ||
    // Safari
    msg.includes('importing a module script failed') ||
    // App Service runs `pm2 serve --spa`, which answers missing /assets/* with index.html (200),
    // so a removed chunk surfaces as a MIME-type error instead of a 404.
    msg.includes('expected a javascript module script') ||
    msg.includes('disallowed mime type') ||
    msg.includes('is not a valid javascript mime type') ||
    // Chrome/Safari network error on script fetch
    (error.name === 'TypeError' && msg.includes('failed to fetch'))
  );
}

/**
 * Runs a dynamic import and handles chunk load failures caused by new deployments.
 *
 * When the app is updated while a user has an active session, the old chunk filenames
 * (e.g., Dialog-abc123.js) no longer exist on the server, so navigating to a lazy-loaded route
 * fails with a chunk/dynamic import error.
 *
 * Policy (error + outdated build => reload): on a chunk-shaped error we first check whether a newer
 * build is deployed. If so, reload once (sessionStorage guard prevents loops) so the browser fetches
 * the new index.html. If the build is the same, or the check fails, we do not reload — the error
 * propagates so the ErrorBoundary can show its fallback with a Reload button.
 *
 * The reload guard is cleared only after a successful import, never on a repeated failure.
 */
export function importWithReload<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn()
    .then((module) => {
      clearReloadGuard();
      return module;
    })
    .catch(async (error: unknown) => {
      // Only consider a reload for chunk/network errors, not for syntax errors, auth failures, etc.
      if (!isChunkLoadError(error)) {
        throw error;
      }

      const outdated = await checkIsOutdated();
      const message = error instanceof Error ? error.message : String(error);

      if (outdated && reloadOnce(`chunk load failed: ${message}`)) {
        // Never resolve so React doesn't try to render while the page is reloading.
        return new Promise<T>(() => {});
      }

      throw error;
    });
}

/**
 * Wraps React.lazy() with {@link importWithReload} to handle chunk load failures after deployments.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithReload(importFn));
}

/**
 * Same as lazyWithRetry but for modules where you need to pick a named export.
 *
 * Usage:
 *   lazyWithRetryNamed(() => import('./Foo'), m => m.Foo)
 */
export function lazyWithRetryNamed<
  M extends Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends React.ComponentType<any>
>(importFn: () => Promise<M>, pick: (mod: M) => T): React.LazyExoticComponent<T> {
  return lazyWithRetry(() => importFn().then((mod) => ({ default: pick(mod) })));
}
